import _ from "lodash";
import React, { useMemo, useCallback, memo, cloneElement } from "react";
import PropTypes from "prop-types";

// Components
import FullScreenLoader from "UIComponents/FullScreenLoader";
import I18nHOC from "UIComponents/I18nHOC";
import AudioRecorderSelector from "UIComponents/AudioRecorderSelector";
import { LabelComponent } from "UnitPlans/components/LabelComponent";
import { FieldComponent } from "UnitPlans/components/IBcomponents";
import CoachMarks from "AppComponents/CoachMarks";
import RemoveSubjectModal from "AppComponents/RemoveSubjectModal";
import CreateDropDown from "Attachment/CreateDropDown";
import WithMiddlePaneHOC from "./WithMiddlePaneHOC";

// Constants
import { ICONS } from "Constants";
import {
  ELEMENT_TYPE_DP_ATL,
  PYP_SUBJECT_SPECIFIC,
  UBD_SUBJECT_SPECIFIC,
  MYP_SUBJECT_SPECIFIC,
  UNIT_TYPE_DP,
  ASSESSMENT_TYPE_AI_TUTOR,
  CURRICULUM_TYPE_DP,
  ASSESSMENT_TYPE_WORKSHEET,
  TASK_TYPE_AI_TUTOR,
  TASK_TYPE_WORKSHEET,
} from "Constants/stringConstants";
import {
  AI_TRANSLATION_LANGUAGES,
  AI_TRANSLATION_LANGUAGES_V2,
  AI_TUTOR_INPUT_METHODS,
  AI_TUTOR_OUTPUT_METHODS,
  ASSESSMENT_TOOLS,
  STUDENT_COMMUNICATION_MODES,
  TUTOR_COMMUNICATION_MODES,
} from "@toddle-edu/common-utils";
import { STANDARD_UIDS } from "modules/Services/ServicesConstants";

// Services
import {
  getMYPObjectiveRubricOptions,
  hideIntercom,
  getUnitPlanFieldValue,
  getSpeechToTextTranscript,
} from "modules/Services";
import {
  getPypElementLabel,
  checkEmptyElementValue,
} from "modules/ElementValueUtils";
import { pypElementMapping } from "Constants/plannerElementMappings";

// Utils
import {
  extractLabelsForTranslation,
  getPYPElementSetFromNodes,
  generateRandomId,
  htmlToText,
  getLanguageSelectorOptions,
} from "Utils";
import {
  getAssessmentToolText,
  getIsIBGradeScaleTool,
  getIsScoreBasedRubricEnabled,
  getSubmissionTemplateUploadStatus,
  getIsEnabledOptimizedSubmissionTemplateDriveFLow,
  getIsAIGradingEnabled,
} from "ClassRoom/modules/utils";
import { getStringWithArchivedLabel } from "Course/modules/Utils";
import { getCurrentAssessmentTypes } from "Platform/modules/PlatformModule";
import {
  getAssessmentSubjects,
  getLtiToolData,
  getRequiredFields,
  getAttachmentGroups,
  getTurnitinAttachments,
  updateAttachmentGroupsWithName,
  getLevelsForSubjectOrCourseBasedSetup,
} from "Assessments/modules/Utils";
import { mutateStepsForWorksheetSinglePageFlow } from "Worksheet/modules/WorksheetUtils";
import {
  getIsLearningGoalsRevampEnabled,
  getIsWorksheetDocEditorEnabled,
} from "Worksheet/modules/WorksheetFeatureFlags";
import {
  getIsLearningCoursesEnabled,
  getIsSubjectBasedGradingEnabled,
  getIsCourseBasedGradingEnabled,
} from "LearningCourses/modules/Utils";
import { getIsAITutorEnhancementsV2Enabled } from "Utils/ACLStoreUtils";
import { getAssessmentLearningGoalsDropdownOptions } from "AppComponents/AssessmentLearningGoals/module/AssessmentLearningGoalsUtils";
import { getLTIName } from "LTIComponent/modules/Utils";
import { getNodeParents } from "Utils/nodeParentHelpers";

// Design System
import {
  Button,
  Alert,
  Modal,
  Checkbox,
  IconButton,
  Dropdown,
  DropdownMenu,
  DSProvider,
  Tooltip,
} from "@toddle-edu/ds-web";
import {
  MicrophoneOutlined,
  AddCircleOutlined,
  DeleteOutlined,
} from "@toddle-edu/ds-icons";
import { fontStyles } from "@toddle-edu/ds-theme";

// External Lib.
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import { Trans } from "react-i18next";
import classNames from "classnames";

// Misc.
import {
  enityEnumToLocalTypeMapping,
  getPlannerFieldMutationParams,
  ResourceDetailsContext,
} from "UnitPlans/modules/UnitPlanModule";
import { getOptionsForNonPlannerFields } from "UnitPlans/modules/Utils";
import {
  getAddLearningGoalsDropdownOptions,
  getOptionsLocale as _getOptionsLocale,
  getSubFields,
} from "UnitPlans/components/LibraryCommonComponents/ResourceEdit/modules/Utils";
import { getFieldLockedObject } from "modules/PusherModule";
import ACLStore from "lib/aclStore";
import {
  updateAIDataInRedux,
  clearFieldUpdateActivityStatus,
  getRemovedSubjectDependencies,
  updateAssessment,
} from "Assessments/modules/AssessmentModule";
import { updateAttachment } from "OrganizationResources/modules/OrganizationResourcesModule";
// Scss
import classes from "./MiddlePane.scss";

const styles = {
  labelComponentStyles: {
    plannerIconProps: {
      isV2: true,
      plannerIconCustomStyle: {
        width: "auto",
        height: "auto",
      },
      variant: "subtle",
    },
    leftContainerStyle: { gap: "8px", margin: "0px" },
    labelIconContainerStyle: {
      margin: 0,
      alignItems: "center",
      minHeight: "28px",
    },
    labelStyle: {
      padding: "0px",
      fontSize: "1.6rem",
      ...fontStyles.demiBold,
      lineHeight: "2rem",
    },
  },
  subTextStyle: {
    position: "relative",
    left: "-26px",
  },
  dropdownMenuStyle: { maxHeight: "254px" },
  alertContainerStyle: {
    zIndex: 1100,
  },
  aiInstructionsVoiceContainerStyle: {
    position: "absolute",
    zIndex: 999,
    width: "max-content",
    right: "12px",
    top: "40px",
  },
};

const MODAL_FIELD_UIDS = ["resources"];

const DIALOGS_INFO = {
  REMOVE_ALL_ASSESSMENT_TOOLS: {
    title: ({ t }) => t("worksheet:remove_all_assessment_tools"),
    body: ({ t }) => t("worksheet:remove_all_assessment_tools_sub_text"),
    confirmButtonText: ({ t }) => t("common:remove"),
    cancelButtonText: ({ t }) => t("common:cancel"),
  },
  MEASURE_ASSESSING: {
    title: ({ t, currentDialogInfo }) => {
      const { assessmentToolText } = getAssessmentToolText({
        t,
        assessmentToolType: _.get(currentDialogInfo, "params.type"),
      });

      return t("classRoom:remove_from_task_with_label", {
        label: assessmentToolText,
      });
    },
    body: ({ t, isDraft, currentDialogInfo, updateCurrentDialogInfo }) => {
      const type = _.get(currentDialogInfo, "params.type");

      if (
        !isDraft &&
        getIsIBGradeScaleTool({
          assessmentToolType: type,
          checkForManual: false,
        })
      ) {
        const isChecked = _.get(currentDialogInfo, "params.isChecked", false);

        const toggleCheckbox = () => {
          updateCurrentDialogInfo({
            ...currentDialogInfo,
            params: {
              ..._.get(currentDialogInfo, "params", {}),
              isChecked: !isChecked,
            },
          });
        };

        return (
          <div className={classes.modalBodyContainer}>
            <div className={classes.modalBody}>
              {t("classRoom:assessment_tool_remove_warning_msg")}
            </div>

            <div className={classes.checkboxContainer} onClick={toggleCheckbox}>
              <Checkbox isChecked={isChecked} />
              <div>
                <Trans i18nKey="classRoom:delete_associated_evaluation_msg">
                  <span>I understand that removing this assessment tool</span>
                  <span>would delete the evaluation associated</span>
                  <span>with it as well</span>
                </Trans>
              </div>
            </div>
          </div>
        );
      } else {
        return t("classRoom:assessment_tool_remove_subtext");
      }
    },
    confirmButtonText: ({ t }) => t("common:remove"),
    cancelButtonText: ({ t }) => t("common:cancel"),
    disabled: ({ currentDialogInfo }) =>
      !_.get(currentDialogInfo, "params.isChecked", true),
  },
  // Generic dialog for removing any subject level (HL, SL, or any other level)
  SUBJECT_LEVEL: {
    title: ({ t, currentDialogInfo }) =>
      t("unitPlan:remove_tag_warning_title", {
        tag: t(`common:${_.get(currentDialogInfo, "params.tag")}`),
      }),
    body: ({ t, currentDialogInfo }) =>
      t("unitPlan:remove_tag_warning_msg", {
        tag: t(`common:${_.get(currentDialogInfo, "params.tag")}`),
        assessmentType: _.get(currentDialogInfo, "params.assessmentTypeLabel"),
      }),
    confirmButtonText: ({ t, currentDialogInfo }) =>
      t("unitPlan:remove_tag", {
        tag: t(`common:${_.get(currentDialogInfo, "params.tag")}`),
      }),
    cancelButtonText: ({ t }) => t("common:cancel"),
  },
};

const getParentNodes = ({ nodes }) => {
  return _.reduce(
    nodes,
    (result, node) => {
      const associatedParents = _.map(
        getNodeParents(node, "SUBJECT"),
        parent => {
          const isArchived = _.get(parent, "isArchived", false);
          return { id: parent.id, label: parent.name, isArchived };
        }
      );

      result = _.unionBy(result, associatedParents, "id");
      return result;
    },
    []
  );
};

const getParentNodesMemoized = _.memoize(
  params => getParentNodes(params),
  params => {
    if (_.isEmpty(params.nodes)) return "empty";

    // Include properties that affect the output
    const nodeParentKeys = _.map(params.nodes, node => {
      return _.chain(getNodeParents(node, "SUBJECT"))
        .map(parent => {
          const isArchived = _.get(parent, "isArchived", false);
          return `${parent.id}:${parent.name}:${isArchived}`;
        })
        .sortBy()
        .join(",")
        .value();
    });

    return _.chain(nodeParentKeys).sortBy().join("|").value();
  }
);

const AI_TUTOR_UPDATE_FIELDS = [
  "description",
  "openingMessage",
  "aiRole",
  "aiRules",
  "tutorLanguage",
  "tutorSecondaryLanguage",
  "studentCommunicationMode",
  "tutorCommunicationMode",
];

class MiddlePane extends React.PureComponent {
  constructor(props) {
    super(props);
    props.customRef(this);
    this.Refs = {};
    this.itemRefs = {};
    this.container = null;
    this.updatedField = {};
    this.nonTurnitinLTIAutoPopulated = new Set();
    this.aiInstructionsMicRef = React.createRef();
    this.state = {
      isLoading: false,
      currentDialogInfo: {
        action: "",
        params: {},
      },
      showSingleAssessmentCopyAlert: true,
    };
  }

  componentWillUnmount = () => {
    const { clearFieldUpdateActivityStatus } = this.props;

    clearFieldUpdateActivityStatus();
  };

  componentDidMount = () => {
    // getExtraRightButtonComponent uses this.Refs to render few fields
    // calling forceUpdate to re-render the component on mount to make sure the this.Refs is available everytime
    this.forceUpdate();
    this.autoSelectSubjectForLearningCourse();
  };

  componentDidUpdate = prevProps => {
    const { assessmentMode } = this.props;

    if (!getIsLearningCoursesEnabled() || assessmentMode !== "create") {
      return;
    }

    const prevSelectedSubjects = _.get(
      _.find(prevProps.resourceFields, { uid: "subjects" }),
      "value",
      []
    );
    const selectedSubjects = this.getResourceFieldValue({
      fieldKey: "subjects",
    });

    const isLearningCourseChanged = !_.isEqual(
      prevProps.learningCourseId,
      this.props.learningCourseId
    );
    const isSubjectClearedAfterSelection =
      !_.isEmpty(prevSelectedSubjects) && _.isEmpty(selectedSubjects);

    if (isLearningCourseChanged || isSubjectClearedAfterSelection) {
      this.autoSelectSubjectForLearningCourse();
    }
  };

  blurFields = ({ field } = {}) => {
    const { uid } = field;
    const fieldRef = this.Refs[uid];

    if (fieldRef) {
      fieldRef.blur({ field });
    }
  };

  isValid = () => {
    let errorCount = 0;
    const { field_list, curriculumProgramType } = this.props;

    const fieldsToValidate = [...getRequiredFields(field_list)];

    if (getIsScoreBasedRubricEnabled() || curriculumProgramType === "IB_DP") {
      fieldsToValidate.push("measureAssessing");
    }

    _.forEach(fieldsToValidate, fieldkey => {
      if (this.Refs[fieldkey]) {
        errorCount += this.Refs[fieldkey]?.isValid?.() ?? 0;
      }
    });
    return errorCount;
  };

  isAITutor = () => {
    const { assessmentType, taskTypeGroup } = this.props;
    return (
      assessmentType === ASSESSMENT_TYPE_AI_TUTOR ||
      taskTypeGroup === TASK_TYPE_AI_TUTOR
    );
  };

  clearTitleFieldErrors = () => {
    const titleFieldRef = this.Refs?.title;
    if (titleFieldRef) {
      if (_.isFunction(titleFieldRef.clearValidationError)) {
        titleFieldRef.clearValidationError();
      } else if (_.isFunction(titleFieldRef.setState)) {
        titleFieldRef.setState({ error: "" });
      }

      if (_.isFunction(titleFieldRef.forceUpdate)) {
        titleFieldRef.forceUpdate();
      }
    }

    if (this.Refs?.title?.clearError) {
      this.Refs.title.clearError();
    }
  };

  updateTurnitinAttachmentsInBackend = async (turnitinAttachments, newName) => {
    const { updateAttachment, portalType } = this.props;

    if (!updateAttachment) return;

    for (const attachment of turnitinAttachments) {
      const attachmentId = attachment.id || attachment.attachmentId;
      await updateAttachment({
        name: newName,
        id: attachmentId,
        portalType,
      });
    }
  };

  handleTurnitinAttachmentRename = async ({ newName, attachmentId, value }) => {
    const {
      resourceFields,
      field_list,
      updateFieldInCache,
      portalType,
      resourceId,
      updateAssessment,
    } = this.props;

    const attachmentGroups = value || getAttachmentGroups();

    const allAttachments = _.flatMap(attachmentGroups, attachmentGroup => {
      const attachments = _.get(attachmentGroup, "attachments", []);
      return _.isEmpty(attachments) ? [attachmentGroup] : attachments;
    });

    const fullAttachmentData = _.find(
      allAttachments,
      att => att.id === attachmentId || att.attachmentId === attachmentId
    );
    const isLTITool = _.get(fullAttachmentData, "metadata.type") === "LTI_TOOL";
    if (!isLTITool) return;

    const isTurnitin =
      _.get(fullAttachmentData, "metadata.service") === "TURNITIN";
    if (!isTurnitin && this.nonTurnitinLTIAutoPopulated.has(attachmentId))
      return;

    const titleFieldObj = _.find(resourceFields, { uid: "title" });
    if (!titleFieldObj) return;

    const currentTitle = _.get(titleFieldObj, "value", "");
    const titleFieldId = _.get(titleFieldObj, "id", "");
    const oldAttachmentName = _.get(fullAttachmentData, "name", "");

    const isTitleEmpty = _.isEmpty(_.trim(currentTitle));

    const wasTitleAutoPopulated =
      !_.isEmpty(oldAttachmentName) &&
      _.trim(currentTitle) === _.trim(oldAttachmentName);

    if (!wasTitleAutoPopulated && !isTitleEmpty) return;

    if (!isTurnitin) {
      this.nonTurnitinLTIAutoPopulated.add(attachmentId);
    }

    this.updateField({
      params: { title: newName },
      isMutation: false,
      isDebounce: false,
      isWarningReq: false,
    });

    const updatedAttachmentGroups = updateAttachmentGroupsWithName(
      attachmentGroups,
      attachmentId,
      newName
    );

    const studentTemplateFieldObj = _.find(resourceFields, {
      uid: "studentTemplate",
    });
    if (studentTemplateFieldObj) {
      updateFieldInCache({
        fieldUID: "studentTemplate",
        params: {
          id: _.get(studentTemplateFieldObj, "id"),
          value: updatedAttachmentGroups,
          uid: "studentTemplate",
        },
        fieldObj: studentTemplateFieldObj,
        fieldTemplateObj: field_list?.["studentTemplate"],
      });
    }

    this.clearTitleFieldErrors();

    if (isTurnitin) {
      await this.updateTurnitinAttachmentsInBackend(
        [fullAttachmentData],
        newName
      );
    }

    if (titleFieldId && resourceId && _.isFunction(updateAssessment)) {
      await updateAssessment({
        assessmentId: resourceId,
        portalType,
        fields: [
          {
            id: titleFieldId,
            fieldUID: "title",
            value: newName,
          },
        ],
      });
    }
  };

  updateField = ({
    params,
    isMutation = false,
    isDebounce = true,
    hasAddRemoveValues = false,
    extraParams = {},
    isWarningReq = true,
  }) => {
    const {
      t,
      portalType,
      resourceFields,
      field_list,
      updateField,
      updateFieldInCache,
      parentType,
      curriculumType,
      assessmentTools,
      resourceFields: assessmentFields,
      templateId,
      assessmentType,
      taskTypeGroup,
      getRemovedSubjectDependencies,
      assessmentMode,
      isLearningGoalsEvaluationEnabled,
      step_list,
      isDraft,
      updateAIDataInRedux,
      resourceId,
      removeAssessmentTool,
      updateAttachment,
    } = this.props;

    _.map(params, (obj, key) => {
      const fieldObj = _.find(resourceFields, { uid: key }) || {};
      const fieldTemplateObj = field_list[key];
      const { id, value: oldValue } = fieldObj;
      const value = { id, value: obj };
      let shouldUpdate = true;

      if (key === "title" && isMutation) {
        const turnitinAttachments = getTurnitinAttachments(resourceFields);

        if (!_.isEmpty(turnitinAttachments) && updateAttachment) {
          this.updateTurnitinAttachmentsInBackend(turnitinAttachments, obj);

          const attachmentGroups = getAttachmentGroups(resourceFields);
          const updatedAttachmentGroups = updateAttachmentGroupsWithName(
            attachmentGroups,
            obj
          );
          const studentTemplateFieldObj = _.find(resourceFields, {
            uid: "studentTemplate",
          });

          if (studentTemplateFieldObj) {
            updateFieldInCache({
              fieldUID: "studentTemplate",
              params: {
                id: _.get(studentTemplateFieldObj, "id"),
                value: updatedAttachmentGroups,
                uid: "studentTemplate",
              },
              fieldObj: studentTemplateFieldObj,
              fieldTemplateObj: field_list?.["studentTemplate"],
            });
          }
        }
      }

      if (isWarningReq) {
        switch (key) {
          case "measureAssessing": {
            const { id, type } = extraParams || {};

            // show warning before removing in edit mode, else remove the assessment tool directly
            if (assessmentMode === "edit") {
              this.updatedField = {
                key,
                newValue: value,
                oldValue,
                ...extraParams,
              };

              this.setState({
                currentDialogInfo: {
                  action: "MEASURE_ASSESSING",
                  params: {
                    type,
                    isChecked:
                      isDraft ||
                      !getIsIBGradeScaleTool({
                        assessmentToolType: type,
                        checkForManual: false,
                      }),
                  },
                },
              });
            } else {
              removeAssessmentTool({
                id,
                type,
                parentId: resourceId,
                parentType,
                portalType,
              });
            }

            return;
          }
          case "subjectLevel": {
            shouldUpdate = this.shouldShowDialogueBox({
              key,
              oldValue,
              newValue: obj,
              fieldObj,
              isDraft,
            });
            break;
          }
          case "subjects": {
            const { subjectDependencies, headerTitle, subTitle } =
              getRemovedSubjectDependencies({
                t,
                curriculumType,
                assessmentTools,
                assessmentFields,
                field_list,
                templateId,
                currentSubjectValue: oldValue,
                updatedSubjectValue: obj,
                assessmentType,
                assessmentMode,
                isLearningGoalsEvaluationEnabled,
                step_list,
              }) || {};

            if (!_.isEmpty(subjectDependencies) && headerTitle && subTitle) {
              this.updatedField = {
                key,
                newValue: obj,
                oldValue,
                hasAddRemoveValues,
                ...extraParams,
              };
              this.setState({
                currentDialogInfo: {
                  action: "REMOVE_SUBJECT",
                  params: {
                    subjectDependencies,
                    headerTitle,
                    subTitle,
                  },
                },
              });
              return;
            }
            break;
          }
        }
      }

      if (
        _.includes(
          [
            "voiceInstruction",
            "studentTemplate",
            "unitPlan",
            "ltiTemplate",
            "openingMessageResource",
          ],
          key
        )
      ) {
        isMutation = false;
      }

      // when AI_TUTOR_UPDATE_FIELDS are updated then a new AI tutor preview chat (proficiency levels) will be generated
      if (this.isAITutor() && _.includes(AI_TUTOR_UPDATE_FIELDS, key)) {
        updateAIDataInRedux({
          shouldGeneratePreviewChat: true,
          updatedFields: [key],
        });
      }

      if (shouldUpdate) {
        updateFieldInCache({
          fieldUID: key,
          params: { ...value, uid: key },
          fieldObj,
          fieldTemplateObj,
          extraParams,
        });

        if (isMutation) {
          const mutationParams = getPlannerFieldMutationParams({
            id,
            oldValue,
            hasAddRemoveValues,
            newValue: obj,
          });

          updateField({
            key: id,
            params: {
              ...(mutationParams || {}),
              portalType,
            },
            type: parentType,
            isDebounce,
            t,
          });
        }
      }
    });
  };

  shouldShowDialogueBox = params => {
    const {
      assessmentMode,
      assessmentType,
      getCurrentAssessmentTypes,
      isDraft,
      t,
    } = this.props;

    const { key, oldValue, newValue, fieldObj } = params;

    const isValueRemoved = _.size(newValue) < _.size(oldValue);

    if (!isDraft && isValueRemoved && assessmentMode === "edit") {
      const genericTags = _.get(
        fieldObj,
        "resolvedMinimalTree.genericTags",
        []
      );

      const value = _.differenceBy(oldValue, newValue);
      const removedTag = _.filter(genericTags, ({ id }) => id == value);
      const removedTagLabel = _.get(_.first(removedTag), "label", "");

      this.updatedField = { key, newValue, oldValue };

      const currentAssessmentTypes = getCurrentAssessmentTypes();
      const { localeLabel = "", label = "" } =
        currentAssessmentTypes?.[assessmentType] || {};
      const assessmentTypeLabel = localeLabel ? t(localeLabel) : label;

      this.setState({
        currentDialogInfo: {
          action: "SUBJECT_LEVEL",
          params: { tag: removedTagLabel, assessmentTypeLabel },
        },
      });
      return false;
    } else {
      return true;
    }
  };

  onBlurInputField = ({ key: uid, id }) => {
    const {
      toggleFocusedField,
      updateAIDataInRedux,
      focusedField,
    } = this.props;

    // on blur if the field is required then re-generate AI tutor preview chat (proficiency levels)
    if (this.isAITutor() && !_.isEmpty(focusedField)) {
      if (_.includes(AI_TUTOR_UPDATE_FIELDS, uid)) {
        updateAIDataInRedux({
          shouldGeneratePreviewChat: true,
          updatedFields: [uid],
        });
      }
    }

    toggleFocusedField({ isFocused: false, focusedField: { uid, id } });
  };

  onFocusInputField = ({ key: uid, id }) => {
    const { toggleFocusedField } = this.props;
    toggleFocusedField({ isFocused: true, focusedField: { uid, id } });
  };

  getOptions = ({
    fieldKey,
    defaultOptions,
    fieldObj,
    value,
    curriculumType,
  }) => {
    const {
      t,
      resourceFields: assessmentFields,
      field_list,
      unitPlanFields,
      unitPlanFieldList,
      unitPlanTemplate,
      subjects,
      courseSubjects,
      prompts,
      plannerElementSets,
    } = this.props;

    switch (fieldKey) {
      case "measureAssessing":
        return this.getMeasureAssessingOptions({ fieldKey, defaultOptions, t });
      case "lois":
        return this.getNotPypChecklistOptions({ fieldKey });
      case "lp":
      case "concepts":
      case "action":
        return this.getPypElementOptions({ fieldKey });
      case "atls":
      case "benchmarks":
        return this.getBenchmarkOptions({ fieldKey });
      case "assessmentType":
        return this.getAssessmentTypeOptions({
          fieldKey,
          value,
          curriculumType,
        });
      case "mypObjectiveRubric":
        return getMYPObjectiveRubricOptions({
          unitPlanFields,
          field_list,
          assessmentFields,
        });

      case "unitPlan":
      case "subjects":
        return this.getSubjectOptions({ fieldKey, fieldObj });

      case "scoreSubject":
        return [
          { label: t("common:none"), value: "" },
          ...(this.getResolveMinimalTreeOptions({
            fieldKey: "subjects",
            fieldObj,
            nodeType: "subjects",
          }) || []),
        ];
      case "subjectLevel":
        return this.getSubjectLevelOptions();
      case "addLearningGoals":
        return getAddLearningGoalsDropdownOptions({
          options: defaultOptions,
          resourceFields: assessmentFields,
          unitPlanTemplate,
          subjects: !_.isEmpty(subjects) ? subjects : courseSubjects,
          fieldList: field_list,
          plannerElementSets,
        });
      case "tutorSecondaryLanguage":
      case "tutorLanguage": {
        const aiLanguages = getIsAITutorEnhancementsV2Enabled()
          ? AI_TRANSLATION_LANGUAGES_V2
          : AI_TRANSLATION_LANGUAGES;

        const languages = _.map(aiLanguages, language => {
          const { id, label } = language;
          return {
            key: _.toString(id),
            value: _.toString(id),
            label,
          };
        });

        if (fieldKey === "tutorSecondaryLanguage" && value) {
          languages.unshift({
            key: "",
            value: "",
            label: t("common:none"),
          });
        }

        return languages;
      }

      case "studentCommunicationMode": {
        const openingMessageResourceKey = "openingMessageResource";

        const openingMessageResourceValue = this.getValue({
          fieldData: _.find(assessmentFields, {
            uid: openingMessageResourceKey,
          }),
          key: openingMessageResourceKey,
          fieldObj: field_list[openingMessageResourceKey],
        });

        return _.map(STUDENT_COMMUNICATION_MODES, mode => {
          return {
            ...mode,
            label: t(mode.localeLabel),
            value: mode.id,
            disabled:
              mode.id === "speaking_only" &&
              !_.isEmpty(openingMessageResourceValue),
            disabledTooltip: t("ai:speaking_only_disabled_tooltip"),
          };
        });
      }
      case "tutorCommunicationMode": {
        return _.map(TUTOR_COMMUNICATION_MODES, mode => {
          return {
            ...mode,
            label: t(mode.localeLabel),
            value: mode.id,
          };
        });
      }

      case "inputMethod": {
        return _.map(AI_TUTOR_INPUT_METHODS, method => {
          return {
            ...method,
            value: method.id,
          };
        });
      }
      case "outputMethod": {
        return _.map(AI_TUTOR_OUTPUT_METHODS, method => {
          return {
            ...method,
            value: method.id,
          };
        });
      }
      case "prompt": {
        const allPrompts = _.reduce(
          prompts,
          (acc, prompt) => {
            const { action, displayText, promptType } = prompt || {};
            if (action && promptType === "display") {
              acc.push({
                label: displayText,
                value: action,
                subText: action,
              });
            }

            return acc;
          },
          []
        );

        return [
          ...allPrompts,
          { label: t("common:none"), value: "", subText: null },
        ];
      }
      case "templateLanguage": {
        const languageOptions = getLanguageSelectorOptions();
        return _.map(languageOptions, ({ key, label }) => {
          return {
            label,
            value: key,
          };
        });
      }
      default: {
        //Handled Planner Elements
        const type = _.get(fieldObj, "config.type");

        const fieldType = _.get(fieldObj, "type", null);

        if (type == "PLANNER_ELEMENT") {
          return this.getPlannerElementOptions({ fieldKey, fieldObj });
        } else if (fieldType === "CheckListWithPopup") {
          return getOptionsForNonPlannerFields({
            fieldKey,
            unitPlanFields,
            unitPlanFieldList,
            fieldObj,
          });
        } else {
          return defaultOptions || [];
        }
      }
    }
  };

  getLabel = ({ fieldKey, value }) => {
    switch (fieldKey) {
      case "lois":
        return this.getNotPypChecklistLabel({ value, fieldKey });
      case "lp":
      case "concepts":
      case "atls":
      case "benchmarks":
      case "action":
        return getPypElementLabel({ value, type: pypElementMapping[fieldKey] });
      default:
        return "";
    }
  };

  getUnitPlanFieldResolvedValue = ({ fieldKey }) => {
    const { unitPlanFields } = this.props;
    return _.get(
      _.find(unitPlanFields, { uid: fieldKey }),
      "resolvedMinimalTree",
      ""
    );
  };

  getResourceFieldValue = ({ fieldKey }) => {
    const { resourceFields } = this.props;
    return _.get(_.find(resourceFields, { uid: fieldKey }), "value", "");
  };

  getResouceFieldResolveValue = ({ fieldKey }) => {
    const { resourceFields } = this.props;
    return _.get(
      _.find(resourceFields, { uid: fieldKey }),
      "resolvedMinimalTree",
      ""
    );
  };

  getMeasureAssessingOptions = ({ fieldKey, defaultOptions, t }) => {
    return this.getOptionsLocale({ options: defaultOptions, key: fieldKey, t });
  };

  getNotPypChecklistOptions = ({ fieldKey }) => {
    const { unitPlanFields } = this.props;
    let unitPlanLois = getUnitPlanFieldValue({ fieldKey, unitPlanFields });
    unitPlanLois = _.filter(unitPlanLois, item => _.trim(item.value) != "");

    return _.map(unitPlanLois, loi => {
      return { label: loi.value, value: loi.id };
    });
  };

  getNotPypChecklistLabel = ({ value, fieldKey }) => {
    const { unitPlanFields } = this.props;
    const unitPlanLois = getUnitPlanFieldValue({ fieldKey, unitPlanFields });
    return _.get(_.find(unitPlanLois, { id: value }), "value", "");
  };

  getPypElementOptions = ({ fieldKey }) => {
    const { unitPlanFields } = this.props;
    const unitPlanPypValues = getUnitPlanFieldValue({
      fieldKey,
      unitPlanFields,
    });
    const fieldValues = this.getResourceFieldValue({ fieldKey });
    const values = _.uniq([
      ...(unitPlanPypValues || []),
      ...(fieldValues || []),
    ]);
    return _.map(values, value => {
      return {
        label: this.getLabel({ fieldKey, value }),
        value,
      };
    });
  };

  getPlannerElementOptions = ({ fieldKey, fieldObj }) => {
    const { plannerElements, t, unitPlanFields } = this.props;

    const plannerElementConfig = _.get(
      fieldObj,
      "config.plannerElementConfig",
      {}
    );

    const { type } = plannerElementConfig;

    const unitPlanFieldValue =
      getUnitPlanFieldValue({ fieldKey, unitPlanFields }) || [];

    const resourceFieldValue = this.getResourceFieldValue({ fieldKey }) || [];

    const resourceSubjects =
      this.getResourceFieldValue({ fieldKey: "subjects" }) || [];

    const unitPlanSubjects =
      getUnitPlanFieldValue({ fieldKey: "subjects", unitPlanFields }) || [];

    const unitPlanSubjectResolvedValues = _.get(
      this.getUnitPlanFieldResolvedValue({
        fieldKey: "subjects",
      }),
      "subjects",
      []
    );

    const unitPlanFieldValueNodes = _.get(
      this.getUnitPlanFieldResolvedValue({
        fieldKey,
      }),
      "nodes",
      []
    );

    const resourceFieldValueNodes = _.get(
      this.getResouceFieldResolveValue({ fieldKey }),
      "nodes",
      []
    );

    const nodes = _.uniqBy(
      [...unitPlanFieldValueNodes, ...resourceFieldValueNodes],
      "id"
    );

    const value = _.uniq([...unitPlanFieldValue, ...resourceFieldValue]);

    let parentNodes = [];
    switch (type) {
      case "MYP_ATL":
      case "UBD_ATL":
        return {
          parentNodes: _.filter(nodes, ({ parent }) => !parent),

          value,
          nodes,
          plannerElementSetData: {
            type,
            parentType: "PLANNER_ELEMENT",
            groupedByType: type,
          },
        };
      case "MYP_OBJECTIVES": {
        parentNodes = getParentNodesMemoized({ nodes });

        if (!_.isEmpty(resourceSubjects)) {
          parentNodes = _.filter(parentNodes, node =>
            _.includes(resourceSubjects, node.id)
          );
        }

        const groupedByType = "SUBJECT";

        return {
          parentNodes,
          value,
          nodes,
          plannerElementSetData: {
            type,
            groupedByType,
            parentType: "PLANNER_ELEMENT",
          },
        };
      }
      case "MYP_LEARNING_STANDARD":
      case "UBD_LEARNING_STANDARD":
      case "DP_SUBJECT_STANDARD":
        parentNodes = getParentNodesMemoized({ nodes });

        if (!_.isEmpty(resourceSubjects)) {
          parentNodes = _.filter(parentNodes, node =>
            _.includes(resourceSubjects, node.id)
          );
        } else {
          parentNodes = _.filter(parentNodes, node =>
            _.includes(unitPlanSubjects, node.id)
          );
        }

        return {
          parentNodes,
          value,
          nodes,
          plannerElementSetData: {
            type,
            parentType: "PLANNER_ELEMENT",
            groupedByType: "SUBJECT",
          },
        };
      case "DP_SYLLABUS":
        parentNodes = _.map(unitPlanSubjectResolvedValues, subject => {
          const isArchived = _.get(subject, "isArchived", false);
          return { id: subject.id, label: subject.name, isArchived };
        });

        return {
          parentNodes,
          value,
          nodes,
          plannerElementSetData: {
            type,
            parentType: "PLANNER_ELEMENT",
            groupedByType: "SUBJECT",
          },
        };
      case "MYP_RELATED_CONCEPT":
      case "MYP_SPECIFIED_CONCEPTS":
        parentNodes = getParentNodesMemoized({ nodes });

        if (!_.isEmpty(resourceSubjects)) {
          parentNodes = _.filter(parentNodes, node =>
            _.includes(resourceSubjects, node.id)
          );
        } else {
          parentNodes = _.filter(parentNodes, node =>
            _.includes(unitPlanSubjects, node.id)
          );
        }

        return {
          parentNodes,
          value,
          nodes,
          plannerElementSetData: {
            type,
            parentType: "PLANNER_ELEMENT",
            groupedByType: "SUBJECT",
          },
        };

      case "DP_PEDAGOGICAL_APPROACH": {
        const plannerElementSet =
          _.find(plannerElements, {
            type: type,
          }) || {};

        const plannerElementNodes = _.get(plannerElementSet, "nodes", []);

        const _resourceFieldValue = _.map(resourceFieldValue, fieldValue => {
          const { id, value, isSelected } = fieldValue || {};

          if (!_.isNil(isSelected)) {
            return {
              id,
              label: value,
              isSelected,
            };
          } else {
            return {
              id: fieldValue,
            };
          }
        });

        const nodes = _.uniqBy(
          [...plannerElementNodes, ..._resourceFieldValue],
          "id"
        );

        return _.map(nodes, node => {
          return { ...node, value: node.id };
        });
      }

      default:
        if (
          // if nested ATLs are enabled, generate nodes and parent nodes
          _.isEqual(type, ELEMENT_TYPE_DP_ATL) &&
          ACLStore.can("FeatureFlag:Nested_DP_Atls")
        ) {
          return {
            parentNodes: _.filter(nodes, ({ parent }) => !parent),
            value,
            nodes,
            plannerElementSetData: {
              type,
              parentType: "PLANNER_ELEMENT",
              groupedByType: type,
            },
          };
        }
        return _.map(nodes, node => {
          return {
            label: node.label,
            value: node.id,
          };
        });
    }
  };

  getResolveMinimalTreeOptions = ({ fieldKey, nodeType, t }) => {
    const unitPlanFieldValueNodes = _.get(
      this.getUnitPlanFieldResolvedValue({
        fieldKey,
      }),
      nodeType,
      []
    );

    return _.map(unitPlanFieldValueNodes, node => {
      const isArchived = _.get(node, "isArchived", false);
      return {
        label:
          node.label ||
          node.name + getStringWithArchivedLabel({ isArchived, t }),

        value: node.id,
      };
    });
  };

  getBenchmarkOptions = ({ fieldKey }) => {
    const { subjects, grades, unitPlanFields } = this.props;
    const unitPlanBenchmarks = getUnitPlanFieldValue({
      fieldKey,
      unitPlanFields,
    });
    const unitPlanResolvedValue = this.getUnitPlanFieldResolvedValue({
      fieldKey,
    });
    const subjectIds = _.map(subjects, item => item.id);
    const resourceFieldResolvedValue =
      this.getResouceFieldResolveValue({ fieldKey }) || [];

    let benchmarkSet;
    switch (fieldKey) {
      case "atls": {
        benchmarkSet = getPYPElementSetFromNodes({
          nodes: _.unionBy(
            _.get(unitPlanResolvedValue, "atls", []),
            _.get(resourceFieldResolvedValue, "atls", []),
            "id"
          ),
        });
        break;
      }
      default: {
        benchmarkSet = getPYPElementSetFromNodes({
          nodes: _.filter(
            _.unionBy(
              _.get(unitPlanResolvedValue, "benchmarks", []),
              _.get(resourceFieldResolvedValue, "benchmarks", []),
              "id"
            ),
            item => _.includes(subjectIds, item.subject)
          ),
        });
      }
    }

    return fieldKey == "atls"
      ? { benchmarkSet }
      : {
          unitPlanBenchmarks,
          benchmarkSet,
          subjects,
          grades,
        };
  };

  getAssessmentTypeOptions = () => {
    const { t, getCurrentAssessmentTypes } = this.props;
    const currentAssessmentTypes = getCurrentAssessmentTypes();

    const assessmentTypeOptionsToShow = _.reduce(
      currentAssessmentTypes,
      (options, { localeLabel, label, isEnabled = false }, uid) => {
        if (!isEnabled) {
          return options;
        }
        const assessmentTypeLabel = localeLabel ? t(localeLabel) : label;
        return [...options, { label: assessmentTypeLabel, value: uid }];
      },
      []
    );

    return assessmentTypeOptionsToShow;
  };

  /**
   * Auto-selects the subject on create when the course has exactly one subject,
   * so the user doesn't have to manually pick from a single-item dropdown.
   *
   * Guard: only runs when the subjects field is actually rendered in the template's
   * step_list. Some assessment types (e.g. QuickTask) include "subjects" in field_list
   * but never wire it into any step, so the field is invisible in the UI. Without this
   * check the auto-select would silently save a subject to the backend even though the
   * user never sees or interacts with the field.
   */
  autoSelectSubjectForLearningCourse = () => {
    const { assessmentMode, step_list } = this.props;

    // Check whether the subjects field is actually rendered in any step of this
    // assessment's template. field_list alone is not sufficient — a field can exist
    // in field_list but be absent from every step (e.g. QuickTask).
    const isSubjectsRendered = _.some(_.values(step_list), step =>
      _.includes(_.get(step, "fields", []), "subjects")
    );

    if (
      !getIsLearningCoursesEnabled() ||
      assessmentMode !== "create" ||
      !isSubjectsRendered
    ) {
      return;
    }

    const fieldKey = "subjects";
    const selectedSubjects = this.getResourceFieldValue({ fieldKey });

    // Only auto-select when nothing is already saved
    if (!_.isEmpty(selectedSubjects)) return;

    const subjectOptions = this.getSubjectOptions({ fieldKey });

    // Auto-select only when there is a single subject — no meaningful choice for the user
    if (_.size(subjectOptions) === 1) {
      const subjectOption = _.first(subjectOptions);
      const subjectId = _.get(subjectOption, "value");
      if (subjectId) {
        this.updateField({
          params: { [fieldKey]: [subjectId] },
          isMutation: true,
          isDebounce: false,
          isWarningReq: false,
          extraParams: { nodes: [subjectOption] },
        });
      }
    }
  };

  getFormattedSubjects = subjects => {
    return _.map(subjects, subject => {
      const { id, name } = subject || {};
      return {
        node: subject,
        label: name,
        value: id,
      };
    });
  };

  getSubjectOptions = ({ fieldKey }) => {
    const {
      resourceFields,
      courseId,
      unitPlanFields,
      curriculumType,
      learningCourseId,
    } = this.props;

    const subjectFieldData = _.find(unitPlanFields, { uid: "subjects" }) || {};
    const unitPlanSubjects = _.get(
      subjectFieldData,
      `resolvedMinimalTree.subjects`,
      []
    );

    const unitSubjects = this.getFormattedSubjects(unitPlanSubjects);
    const assessmentSubjects = this.getFormattedSubjects(
      this.getResouceFieldResolveValue({ fieldKey })?.subjects
    );

    const unitPlan = _.find(resourceFields, { uid: "unitPlan" })?.value;

    const filteredUnitSubjects = _.filter(
      unitSubjects,
      subject => !_.get(subject, "node.isArchived", false)
    );

    if (unitPlan) {
      // combine unit subjects + previosuly selected subjects
      return _.uniqBy(
        [...filteredUnitSubjects, ...assessmentSubjects],
        "value"
      );
    } else {
      const courseSubjects = this.getFormattedSubjects(
        getAssessmentSubjects({
          courseId,
          learningCourseId,
        })
      );

      const filteredCourseSubjects = _.filter(
        courseSubjects,
        subject => !_.get(subject, "node.isArchived", false)
      );

      // combine course subjects + previosuly selected subjects
      return _.uniqBy(
        [...filteredCourseSubjects, ...assessmentSubjects],
        "value"
      );
    }
  };

  getSubjectLevelOptions = () => {
    const { unitPlanId, courseId, learningCourseId } = this.props;

    if (
      getIsLearningCoursesEnabled() &&
      (getIsSubjectBasedGradingEnabled() || getIsCourseBasedGradingEnabled())
    ) {
      return getLevelsForSubjectOrCourseBasedSetup({ learningCourseId });
    }

    let subject;
    if (!_.isEmpty(unitPlanId)) {
      subject = _.first(
        _.get(
          this.getUnitPlanFieldResolvedValue({
            fieldKey: "subjects",
          }),
          "subjects",
          []
        )
      );
    } else {
      subject = _.first(
        getAssessmentSubjects({
          courseId,
          learningCourseId,
        })
      );

      // if getAssessmentSubjects is empty but subjects were selected in the assessment then use the selected subjects as options
      if (_.isEmpty(subject)) {
        subject = _.first(
          this.getResouceFieldResolveValue({ fieldKey: "subjects" })?.subjects
        );
      }
    }

    return _.orderBy(
      _.map(_.get(subject, "variants", []), variant => {
        const { level } = variant;
        return { label: level.label, value: level.id };
      }),
      "label",
      "desc"
    );
  };

  getLabelSubtext = ({ key }) => {
    const { assessmentToolId, field_list, t } = this.props;
    const { config: { subtext } = {} } = field_list[key] ? field_list[key] : {};

    if (_.get(_.find(ASSESSMENT_TOOLS, { key }), "hasTemplate", false)) {
      const name = _.get(_.find(ASSESSMENT_TOOLS, { key }), "locale", "");
      const isCreating = !assessmentToolId;
      return isCreating
        ? `${t("unitPlan:create_new_template_msg_with_label", {
            label: t(name),
          })}`
        : subtext;
    } else return subtext;
  };

  getFieldLockedObject = ({ key }) => {
    const { lockedFields } = this.props;
    let uids = [];
    switch (key) {
      case "rubric":
      case "exemplar":
      case "anecdotal":
      case "checklist":
      case "measureAssessing":
      case "singlePointRubric":
        uids = [
          "rubric",
          "exemplar",
          "anecdotal",
          "checklist",
          "measureAssessing",
          "singlePointRubric",
        ];
        break;
      default:
        uids = [key];
    }

    return getFieldLockedObject({ uids, lockedFields });
  };

  isSubjectSpecificUnit = () => {
    const { unitType } = this.props;

    const subjectSpecificTypes = [
      PYP_SUBJECT_SPECIFIC,
      UBD_SUBJECT_SPECIFIC,
      MYP_SUBJECT_SPECIFIC,
      UNIT_TYPE_DP,
    ];

    return !unitType || _.includes(subjectSpecificTypes, unitType);
  };

  getIsDisabled = ({ fieldKey, fieldObj }) => {
    switch (fieldKey) {
      case "subjects": {
        const subjectOptions = this.getSubjectOptions({ fieldKey, fieldObj });
        const selectedSubjects = this.getResourceFieldValue({ fieldKey });

        // if there is only one option to select and it is already selected then disable the field
        return (
          this.isSubjectSpecificUnit() &&
          _.size(subjectOptions) === 1 &&
          _.size(selectedSubjects) === 1
        );
      }
      default:
        return false;
    }
  };

  setFieldRef = (key, ref) => {
    this.Refs[key] = ref;
  };

  checkIsModalField = key => {
    return _.includes(MODAL_FIELD_UIDS, key);
  };

  renderCoachMarks = ({ id, index, resourceIndex, resourceType }) => {
    return (
      index == resourceIndex && (
        <CoachMarks
          type={`RESOURCE`}
          params={{ target: `#COACHMARKS_RESOURCE_${id}`, resourceType }}
          continuous={false}
        />
      )
    );
  };

  /**
   * Function code moved to utils for reusability.
   * This function is still used in other components via `ref`.
   */
  getOptionsLocale = args => {
    return _getOptionsLocale(args);
  };

  processLTIAttachments = (groups, isPost = false, currentTitle = "") => {
    return _.map(groups, group => {
      const hasTurnitinAttachment = _.some(
        group.attachments || [],
        att =>
          _.get(att, "metadata.type") === "LTI_TOOL" &&
          _.get(att, "metadata.service") === "TURNITIN"
      );

      if (hasTurnitinAttachment && !_.isEmpty(currentTitle)) {
        return {
          ...group,
          ...(isPost && { isPost: true }),
          attachments: _.map(group.attachments, att => {
            if (
              _.get(att, "metadata.type") === "LTI_TOOL" &&
              _.get(att, "metadata.service") === "TURNITIN"
            ) {
              return {
                ...att,
                name: currentTitle,
                metadata: {
                  ...att.metadata,
                  lineitem: {
                    ..._.get(att, "metadata.lineitem", {}),
                    label: currentTitle,
                  },
                },
              };
            }
            return att;
          }),
        };
      }

      return { ...group, ...(isPost && { isPost: true }) };
    });
  };

  getValue = ({ fieldData, key, fieldObj }) => {
    const { resourceFields } = this.props;
    const titleField = _.find(resourceFields, { uid: "title" });
    const currentTitle = _.get(titleField, "value", "");

    switch (key) {
      case "voiceInstruction":
        return _.get(fieldData, "resolvedMinimalTree.attachment", "");

      case "studentTemplate":
        return this.processLTIAttachments(
          _.get(fieldData, "resolvedMinimalTree.attachmentGroups", []),
          true,
          currentTitle
        );

      case "openingMessageResource": {
        return _.map(
          _.get(fieldData, "resolvedMinimalTree.attachmentGroups", []),
          group => ({ ...group, isPost: true })
        );
      }

      case "ltiTemplate":
        return this.processLTIAttachments(
          _.get(fieldData, "resolvedMinimalTree.attachmentGroups", []),
          false,
          currentTitle
        );

      case "pedagogicalApproachDP": {
        const value = _.get(fieldData, "value", []);
        if (_.every(value, item => _.has(item, "value"))) {
          return _.map(
            _.filter(value, item => _.get(item, "isSelected", false)),
            "id"
          );
        }
        return value;
      }

      case "aiRole": {
        const value = _.get(fieldData, "value", []);
        return htmlToText(value, { wordwrap: null });
      }
      default: {
        /**
         * this "if condition" will add default options which are present in template
         * in the backend with data structure like [{ id, value,isSelected },{...}]
         * which is for UIInputList.
         *
         * when it will run ?
         * -> It will run only when value for field is null. So, this is the dependency from backend data.
         * We have another dependency from template to run this "if condition". To make run this condition,
         * in field you have to have options with [{labe,value}] form, showDefaultTemplateOptions boolean, and field type
         * should be "InputNumberList".
         */
        if (
          !_.isEmpty(fieldData) &&
          fieldObj?.type === "InputNumberList" &&
          _.get(fieldObj, "config.showDefaultTemplateOptions", false) &&
          !fieldData?.value
        ) {
          const options = _.map(
            _.get(fieldObj, "config.options", []),
            option => ({
              id: generateRandomId(),
              isSelected: false,
              value: option.label,
            })
          );
          this.updateField({
            extraParams: {},
            hasAddRemoveValues: false,
            isDebounce: true,
            isWarningReq: true,
            isMutation: true,
            params: { [key]: options },
          });
          return options;
        }
        return _.get(fieldData, "value", "");
      }
    }
  };

  getDependentFieldValue = ({ dependentFieldId }) => {
    const { field_list, resourceFields } = this.props;
    const dependentFieldData = _.find(resourceFields, {
      uid: dependentFieldId,
    });

    if (_.isEmpty(dependentFieldData)) {
      return null;
    }

    return this.getValue({
      fieldData: dependentFieldData,
      key: dependentFieldId,
      fieldObj: field_list[dependentFieldId],
    });
  };

  getNewDropdownPopupContainer = () => document.body;

  handleAiInstructionsTranscription = async ({ attachment, value }) => {
    const { getSpeechToTextTranscript, resourceId } = this.props;
    const transcriptionPayload = {
      entityId: resourceId,
      entityType: "ASSESSMENT",
      blobAttachment: attachment,
    };
    const transcript = (await getSpeechToTextTranscript(transcriptionPayload))
      ?.transcript;
    if (transcript) {
      const updatedValue = value ? `${value} ${transcript}` : transcript;
      this.updateField({
        params: { aiInstructions: updatedValue },
        isMutation: true,
        isDebounce: false,
      });
    }
  };

  renderAiInstructionsSTT = value => {
    const { t, parentType, resourceId, testId } = this.props;

    return (
      <AudioRecorderSelector
        showValue={false}
        version="inline"
        showProgress={false}
        parentId={resourceId}
        parentType={parentType}
        header={t("common:add_voice_message")}
        onBlobAttachmentRecieved={attachment =>
          this.handleAiInstructionsTranscription({ attachment, value })
        }
        recordButtonRef={this.aiInstructionsMicRef}
        buttonComponent={
          <IconButton
            dsVersion="2.0"
            variant="neutral"
            type="outlined"
            size="small"
            icon={<MicrophoneOutlined />}
            aria-label={t("common:record_voice")}
            testId={testId ? `${testId}-aiInstructions-microphone` : undefined}
            ref={this.aiInstructionsMicRef}
          />
        }
        containerStyle={styles.aiInstructionsVoiceContainerStyle}
        alertContainerStyle={styles.alertContainerStyle}
      />
    );
  };

  getExtraRightButtonComponent = params => {
    const { key, value, fieldId, fieldList, options } = params;
    const {
      t,
      parentType,
      resourceId,
      isUnitPlanTemplateLoading,
      portalType,
      resourceFields,
      unitPlanTemplate,
      subjects,
      courseSubjects,
      field_list,
      testId,
      courseId,
      curriculumProgramId,
      ltiDropDownOptions,
      newWorksheetBasicDetailManager,
      unitCourseId,
      curriculumProgramType,
      plannerElementSets,
      assessmentGradeId,
      assessmentType,
      taskTypeGroup,
    } = this.props;

    const isWorksheet =
      assessmentType === ASSESSMENT_TYPE_WORKSHEET ||
      taskTypeGroup === TASK_TYPE_WORKSHEET;

    switch (key) {
      case "voiceInstruction": {
        const { isUploading, props: { updateInputField } = {} } =
          this.Refs?.[key] || {};

        const CustomButtonComponent = props => {
          const { isRecording } = props;

          return (
            <IconButton
              icon={<MicrophoneOutlined variant="subtle" />}
              _legacy_variant={"plain"}
              variant="neutral"
              type="plain"
              size={"small"}
              disabled={isRecording}
            />
          );
        };

        return _.isEmpty(value) && !isUploading?.() ? (
          <AudioRecorderSelector
            name={key}
            value={value}
            fieldId={fieldId}
            parentId={resourceId}
            parentType={parentType}
            showValue={false}
            buttonComponent={<CustomButtonComponent />}
            alertContainerStyle={styles.alertContainerStyle}
            updateInputField={updateInputField}
            portalType={portalType}
            testId={testId}
            version={
              ACLStore.can("FeatureFlag:ShowAudioRecorderV5") ? "v5" : undefined
            }
          />
        ) : null;
      }

      case "studentTemplate":
      case "submissionTemplate":
      case "openingMessageResource": {
        const { config = {} } = fieldList || {};
        const { fieldTypeConfig, options, sourceType } = config || {};
        const { dropDownOptions: dropDownOptionsWithoutIcons, ...rest } =
          options || {};

        const dropDownOptions = _.map(dropDownOptionsWithoutIcons, option => {
          const Icon = _.get(ICONS, option?.icon);

          if (Icon) {
            return {
              ...option,
              icon: <Icon size={"xx-small"} />,
            };
          }

          return option;
        });

        const { isLtiToolAdded: ltiToolAttachmentAdded } = getLtiToolData({
          resourceFields,
        });

        const containsScoreTool = _.some(
          _.get(this.props.assessmentTools, "edges"),
          edge => _.isEqual(_.get(edge, "node.assessmentToolType"), "SCORE")
        );

        const createLTIScoreTool =
          !containsScoreTool &&
          _.some(
            _.get(field_list, "measureAssessing.config.options"),
            option => {
              return _.isEqual("SCORE", option.key);
            }
          );

        const getPopupContainer =
          key !== "openingMessageResource"
            ? this.getNewDropdownPopupContainer
            : undefined;

        let disabled = false,
          disabledTooltip = "";

        if (key === "openingMessageResource") {
          const fieldData = _.find(resourceFields, { uid: key });
          const optionValue = this.getValue({
            fieldData,
            key,
            fieldObj: field_list[key],
          });
          disabled = !_.isEmpty(optionValue);

          if (!disabled) {
            const studentCommunicationModeKey = "studentCommunicationMode";

            const studentCommunicationModeValue = this.getValue({
              fieldData: _.find(resourceFields, {
                uid: studentCommunicationModeKey,
              }),
              key: studentCommunicationModeKey,
              fieldObj: field_list[studentCommunicationModeKey],
            });

            disabled = studentCommunicationModeValue === "speaking_only";

            if (disabled) {
              disabledTooltip = t("ai:disable_add_resource_button_tooltip");
            }
          }
        }

        return (
          <CreateDropDown
            {...rest}
            dsVersion="2.0"
            {...fieldTypeConfig}
            addAttachment={this.Refs[key]?.addAttachment}
            parentId={fieldId}
            sourceType={sourceType}
            dropDownOptions={dropDownOptions}
            getNewDropdownPopupContainer={getPopupContainer}
            portalType={portalType}
            assessmentId={resourceId}
            courseId={courseId || unitCourseId}
            curriculumId={curriculumProgramId}
            parentType={parentType}
            testId={testId}
            ltiDropDownOptions={ltiDropDownOptions}
            ltiToolAttachmentAdded={ltiToolAttachmentAdded}
            createLTIScoreTool={createLTIScoreTool}
            assessmentGradeId={assessmentGradeId}
            disabled={disabled}
            disabledTooltip={disabledTooltip}
          />
        );
      }

      case "ltiTemplate": {
        const { config = {} } = fieldList || {};
        const { fieldTypeConfig, options, sourceType } = config || {};

        //check if LTI tool attachment is added
        //if true, disable lti integrations options
        const { isLtiToolAdded: ltiToolAttachmentAdded } = getLtiToolData({
          resourceFields,
        });
        const containsScoreTool = _.some(
          _.get(this.props.assessmentTools, "edges"),
          edge => _.isEqual(_.get(edge, "node.assessmentToolType"), "SCORE")
        );

        const createLTIScoreTool =
          !containsScoreTool &&
          _.some(
            _.get(field_list, "measureAssessing.config.options"),
            option => {
              return _.isEqual("SCORE", option.key);
            }
          );
        return !ltiToolAttachmentAdded ? (
          <CreateDropDown
            {...(options || {})}
            {...fieldTypeConfig}
            addAttachment={this.Refs[key]?.addAttachment}
            parentId={fieldId}
            sourceType={sourceType}
            dropDownOptions={[]}
            getNewDropdownPopupContainer={this.getNewDropdownPopupContainer}
            portalType={portalType}
            assessmentId={resourceId}
            courseId={courseId || unitCourseId}
            curriculumId={curriculumProgramId}
            createLTIScoreTool={createLTIScoreTool}
            parentType={parentType}
            testId={testId}
            ltiDropDownOptions={ltiDropDownOptions}
            assessmentGradeId={assessmentGradeId}
          />
        ) : null;
      }
      case "measureAssessing": {
        const {
          assessmentTools = {},
          resourceFields: assessmentFields,
          gradeScales,
        } = this.props;

        return this.Refs?.["measureAssessing"]?.renderDropdown?.({
          assessmentTools,
          assessmentFields,
          gradeScales,
        });
      }
      case "addLearningGoals": {
        const isLearningGoalsRevampField = getIsLearningGoalsRevampEnabled();

        const learningGoalsOptions = getAddLearningGoalsDropdownOptions({
          options,
          resourceFields,
          unitPlanTemplate,
          subjects: !_.isEmpty(subjects) ? subjects : courseSubjects,
          fieldList: field_list,
          plannerElementSets,
        });

        // Old dropdown logic (when learning goals revamp is disabled)
        const isSubjectBasedGrading = getIsSubjectBasedGradingEnabled();

        const selectedSubjects = this.getResourceFieldValue({
          fieldKey: "subjects",
        });

        // When learning goals revamp is enabled, render the new dropdown
        if (isLearningGoalsRevampField) {
          const dropdownTriggerElement = (
            <IconButton
              icon={<AddCircleOutlined variant="subtle" />}
              size={"small"}
              _legacy_variant={"plain"}
              variant="neutral"
              type="plain"
              testId={`${testId}-addLearningGoals-actionMenu`}
            />
          );

          const disableStandardsOption =
            _.isEmpty(selectedSubjects) && isSubjectBasedGrading;

          const learningGoalsDropdownOptions = getAssessmentLearningGoalsDropdownOptions(
            {
              t,
              learningGoalsOptions,
              curriculumType: curriculumProgramType,
              disableStandardsOption,
            }
          );

          return (
            <Dropdown
              overlay={
                <DropdownMenu
                  dsVersion="2.0"
                  onClick={this.Refs?.[key]?.onDropdownItemClick}
                  testId={`${testId}-addLearningGoals-actionMenu`}
                  options={learningGoalsDropdownOptions}
                />
              }
              disabled={isUnitPlanTemplateLoading}
              placement={"bottomRight"}
            >
              {dropdownTriggerElement}
            </Dropdown>
          );
        }

        // Disable STANDARD_UIDS option if no subjects are selected in subject-based grading
        const updatedDropdownOptions = _.map(learningGoalsOptions, option => {
          const optionKey = _.get(option, "key", "");
          if (
            _.isEmpty(selectedSubjects) &&
            isSubjectBasedGrading &&
            STANDARD_UIDS[curriculumProgramType] === optionKey
          ) {
            return {
              ...option,
              disabled: true,
              disabledTooltip: t("classRoom:add_subject_to_tag_with_label", {
                label: option?.label || t("common:standard_other"),
              }),
              tooltipPlacement: "left",
            };
          }

          return option;
        });

        const dropdownTriggerElement = (
          <IconButton
            icon={<AddCircleOutlined variant="subtle" />}
            size={"small"}
            _legacy_variant={"plain"}
            variant="neutral"
            type="plain"
            testId={`${testId}-addLearningGoals-actionMenu`}
          />
        );

        // If there is only one option then directly open modal on click of button
        // This is to avoid showing dropdown menu when there is only one option
        if (_.size(updatedDropdownOptions) === 1) {
          const soleOption = _.first(updatedDropdownOptions);
          const optionKey = _.get(soleOption, "key", "");

          const clonedDropdownTriggerElement = cloneElement(
            dropdownTriggerElement,
            {
              onClick: () =>
                this.Refs?.[key]?.onDropdownItemClick({ key: optionKey }),
              disabled: isUnitPlanTemplateLoading,
            }
          );

          // in case of subject based grading when no subjects are selected in the assignment
          // disable the standard learning goal option and show a tooltip to add subjects

          if (
            _.isEmpty(selectedSubjects) &&
            isSubjectBasedGrading &&
            STANDARD_UIDS[curriculumProgramType] === optionKey
          ) {
            return (
              <Tooltip
                placement={"top"}
                tooltip={t("classRoom:add_subject_to_tag_with_label", {
                  label: soleOption?.label || t("common:standard_other"),
                })}
              >
                {cloneElement(clonedDropdownTriggerElement, { disabled: true })}
              </Tooltip>
            );
          }

          return clonedDropdownTriggerElement;
        }

        return (
          <Dropdown
            overlay={
              <DropdownMenu
                options={updatedDropdownOptions}
                onClick={this.Refs?.[key]?.onDropdownItemClick}
                testId={`${testId}-addLearningGoals-actionMenu`}
                style={styles.dropdownMenuStyle}
              />
            }
            disabled={isUnitPlanTemplateLoading}
            placement={"bottomRight"}
          >
            {dropdownTriggerElement}
          </Dropdown>
        );
      }
      case "description": {
        if (isWorksheet) {
          const clearAndCloseInstruction = () => {
            const setWorksheetBasicDetailsInfo = _.get(
              newWorksheetBasicDetailManager,
              "setWorksheetBasicDetailsInfo",
              _.noop
            );
            this.updateField({
              params: { description: "" },
              isMutation: true,
              isDebounce: true,
            });
            setTimeout(() => {
              this.updateField({
                params: { description: "" },
                isMutation: true,
                isDebounce: true,
              });
            }, 1250);
            setWorksheetBasicDetailsInfo(prev => ({
              ...prev,
              showInstructions: false,
            }));
          };
          return (
            <IconButton
              dsVersion="2.0"
              type="plain"
              variant="neutral"
              size="small"
              icon={<DeleteOutlined />}
              onClick={clearAndCloseInstruction}
              testId="worksheet-basic-details-instruction-close-icon"
            />
          );
        }
      }
    }
  };

  // * Removing all assessment tools one by one in sequence
  removeAllAssessmentTools = async assessmentToolsToRemove => {
    this.setState({
      currentDialogInfo: {
        action: "REMOVE_ALL_ASSESSMENT_TOOLS",
        params: assessmentToolsToRemove,
      },
    });
  };

  renderFieldComponent = fields => {
    const {
      field_list,
      resourceFields,
      createAttachment,
      deleteAttachment,
      resourceId,
      parentType,
      t,
      readOnly,
      unitPlanId,
      isLocalisedTemplate,
      portalType,
      localisedTemplateId,
      curriculumType,
      assessmentType,
      taskTypeGroup,
      assessmentTools,
      orgId,
      unitPlanFields,
      currentRightPane,
      onHelpClick,
      currentFieldKey,
      helpEnabledFields,
      resourceFields: assessmentFields,
      unitPlanFieldList,
      assessmentMode,
      sourceFrom,
      templateId,
      academicTermId,
      category,
      globalCategories,
      isLearningGoalsEvaluationEnabled,
      currentSubject,
      selectedCourseId,
      weightage,
      preSelectedUnitPlan,
      creationMode,
      isFromUnitPlan,
      step_list,
      isDraft,
      assignmentId,
      testId = "assignmentCreation",
      curriculumProgram,
      isToddleLibraryPortal,
      learningCourseId,
      gradeScales,
      courseId,
      unitCourseId,
    } = this.props;

    return _.map(fields, (key, index) => {
      const {
        type,
        viewType,
        config: {
          placeholder,
          emptyText,
          styles: fieldStyles,
          hasAddRemoveValues = false,
          isRequired = false,
          prompts,
          sourceType,
          dependentFieldId,
          icon,
          fieldTypeConfig,
          viewLabel,
          viewLabelLocale,
          plannerElementConfig = {},
          renderFieldIfEmpty = true,
        } = {},
      } = field_list[key] ? field_list[key] : {};

      let { config: { label, options } = {} } = field_list[key]
        ? field_list[key]
        : {};

      if (label === "Name of the Assessment" && key === "title") {
        label = !isLocalisedTemplate
          ? label
          : label &&
            t(
              extractLabelsForTranslation({
                keys: ["le", localisedTemplateId, "assessment", key, "label"],
              })
            );
      }

      options = this.getOptionsLocale({ options, key, t });

      const fieldData = _.find(resourceFields, { uid: key });
      const fieldId = _.get(fieldData, "id", "");
      const subText = this.getLabelSubtext({ key });

      const resourceIndex = _.findIndex(
        fields,
        keyName => keyName == "resources"
      );
      const value = this.getValue({
        fieldData,
        key,
        fieldObj: field_list[key],
      });

      let dependentFieldValue;
      if (dependentFieldId) {
        dependentFieldValue = this.getDependentFieldValue({
          dependentFieldId,
        });
      }

      const fieldOptions = this.getOptions({
        fieldKey: key,
        defaultOptions: options,
        fieldObj: field_list[key],
        value,
        curriculumType,
      });

      // subject level field will be present in all templates, whether it's DP or non-DP
      // skip field when there are no runtime options, regardless of curriculum type
      const isSubjectLevel = key === "subjectLevel";
      if (isSubjectLevel && _.isEmpty(fieldOptions)) {
        return null;
      }
      const effectiveIsRequired = isSubjectLevel ? true : isRequired;

      let fieldComponentType = type;

      const learningGoalFields = _.chain(field_list)
        .get("addLearningGoals.config.options", [])
        .map("key")
        .value();

      if (_.includes(learningGoalFields, key)) {
        fieldComponentType = viewType;
      }

      //renderFieldIfEmpty is false for learning goals fields
      if (!renderFieldIfEmpty) {
        const isValueNotEmpty = checkEmptyElementValue({
          fieldKey: key,
          data: { allFields: resourceFields },
          unitPlanFields,
          fieldObj: field_list[key],
          ignoreUnitPlanSubjectCheck: true,
          checkOnlyValueKeysForBenchmarks: true,
        });
        if (!isValueNotEmpty) {
          return null;
        }
      }

      const submissionTemplateAlertArr = [];
      const submissionTemplateLoaderArr = [];

      if (
        assessmentMode === "edit" &&
        key === "studentTemplate" &&
        assignmentId &&
        getIsEnabledOptimizedSubmissionTemplateDriveFLow()
      ) {
        const submissionTemplateGoogleAttachments = _.filter(
          value,
          attachmentGroup =>
            _.get(
              attachmentGroup,
              "attachments[0].metadata.serviceType",
              ""
            ) === "GOOGLE"
        );

        for (const groupAttachments of submissionTemplateGoogleAttachments) {
          const id = _.get(groupAttachments, "attachments[0].id", "");

          const {
            isStudentTemplateUploadFailed,
            isStudentTemplateUploading,
          } = getSubmissionTemplateUploadStatus(groupAttachments);

          if (isStudentTemplateUploadFailed) {
            submissionTemplateAlertArr.push(id);
          } else if (isStudentTemplateUploading) {
            submissionTemplateLoaderArr.push(id);
          }
        }
      }

      const customErrorClass = classNames({
        [classes.customErrorClass]: true,
        [classes.displayErrorNone]: key === "aiInstructions",
      });

      const isWorksheet =
        assessmentType === ASSESSMENT_TYPE_WORKSHEET ||
        taskTypeGroup === TASK_TYPE_WORKSHEET;
      const richTextBottomToolbar =
        _.includes(["RichText", "RichText_Ported"], fieldComponentType) &&
        isWorksheet &&
        _.includes(["edit", "create"], assessmentMode);

      const showDocEditor = isWorksheet && getIsWorksheetDocEditorEnabled();

      return (
        <div
          id={key}
          className={classes.item}
          key={key}
          style={_.get(fieldStyles, "style", {})}
          ref={ref => {
            this.itemRefs[key] = ref;
          }}
        >
          {this.renderCoachMarks({
            id: fieldId,
            key,
            index,
            resourceIndex,
            resourceType: parentType,
          })}

          {key === "aiInstructions" && this.renderAiInstructionsSTT(value)}
          <WrapperFieldLabelComponent
            t={t}
            portalType={portalType}
            readOnly={readOnly}
            assessmentMode={assessmentMode}
            assessmentType={assessmentType}
            createAttachment={createAttachment}
            deleteAttachment={deleteAttachment}
            fieldLockedObject={this.getFieldLockedObject({
              key,
            })}
            subFields={getSubFields({
              fieldKey: key,
              fieldObj: field_list[key],
              unitPlanFields,
            })}
            unitPlanId={unitPlanId}
            isModalField={this.checkIsModalField(key)}
            customRef={this.setFieldRef}
            type={fieldComponentType}
            label={label}
            subText={subText}
            assessmentTools={assessmentTools}
            hasAddRemoveValues={hasAddRemoveValues}
            extraRightButtonComponent={this.getExtraRightButtonComponent({
              key,
              value,
              fieldId,
              fieldList: field_list?.[key],
              options,
            })}
            parentId={resourceId}
            setScrollToBottom={this.setScrollToBottom}
            parentType={parentType}
            isRequired={effectiveIsRequired}
            emptyText={emptyText}
            attachments={_.get(fieldData, "attachments")}
            value={JSON.stringify(value)}
            resolvedValue={_.get(fieldData, "resolvedMinimalTree")}
            fieldId={fieldId}
            fieldKey={key}
            defaultOptions={JSON.stringify(fieldOptions)}
            placeholder={placeholder}
            updateInputField={this.updateField}
            updateFieldInCache={this.props.updateFieldInCache}
            onBlurInputField={this.onBlurInputField}
            onFocusInputField={this.onFocusInputField}
            styles={fieldStyles}
            prompts={prompts}
            sourceType={sourceType}
            fieldTypeConfig={fieldTypeConfig}
            dependentFieldValue={dependentFieldValue}
            orgId={orgId}
            curriculumType={curriculumType}
            unitPlanFields={unitPlanFields}
            field_list={field_list}
            currentFieldKey={currentFieldKey}
            assessmentFields={assessmentFields}
            unitPlanFieldList={unitPlanFieldList}
            resourceFields={resourceFields}
            currentRightPane={currentRightPane}
            onHelpClick={onHelpClick}
            helpEnabledFields={helpEnabledFields}
            sourceFrom={sourceFrom}
            icon={icon}
            customErrorClass={customErrorClass}
            disabled={this.getIsDisabled({
              fieldKey: key,
              fieldObj: field_list[key],
            })}
            getNewDropdownPopupContainer={this.getNewDropdownPopupContainer}
            templateId={templateId}
            viewLabel={viewLabel}
            viewLabelLocale={viewLabelLocale}
            plannerElementConfig={plannerElementConfig}
            academicTermId={academicTermId}
            category={category}
            globalCategories={globalCategories}
            isLearningGoalsEvaluationEnabled={isLearningGoalsEvaluationEnabled}
            currentSubject={currentSubject}
            selectedCourseId={selectedCourseId}
            weightage={weightage}
            preSelectedUnitPlan={preSelectedUnitPlan}
            creationMode={creationMode}
            isFromUnitPlan={isFromUnitPlan}
            testId={`${testId}-${key}`}
            step_list={step_list}
            isDraft={isDraft}
            assignmentId={assignmentId}
            submissionTemplateAlertArr={submissionTemplateAlertArr}
            submissionTemplateLoaderArr={submissionTemplateLoaderArr}
            curriculumProgram={curriculumProgram}
            isToddleLibraryPortal={isToddleLibraryPortal}
            learningCourseId={learningCourseId}
            removeAllAssessmentTools={this.removeAllAssessmentTools}
            gradeScales={gradeScales}
            courseId={courseId}
            unitCourseId={unitCourseId}
            {...(richTextBottomToolbar ? { toolbarPosition: "bottom" } : {})}
            name={key}
            onTurnitinAttachmentRename={this.handleTurnitinAttachmentRename}
            updateAttachment={this.props.updateAttachment}
            showDocEditor={showDocEditor}
          />
        </div>
      );
    });
  };

  toggleDeleteDialogueBoxDisplay = () => {
    this.setState({ currentDialogInfo: { action: "", params: {} } });
  };

  updateCurrentDialogInfo = currentDialogInfo => {
    this.setState({ currentDialogInfo });
  };

  onConfirmUpdate = async () => {
    const { currentDialogInfo } = this.state;
    const { portalType, field_list, resourceId, parentType, t } = this.props;

    if (_.isEqual("REMOVE_ALL_ASSESSMENT_TOOLS", currentDialogInfo?.action)) {
      this.setState({ isLoading: true });

      const { newWorksheetBasicDetailManager } = this.props;
      const assessmentToolsToRemove = _.get(currentDialogInfo, "params", []);

      // Remove assessment tools one by one in sequence
      for (const toolToRemove of assessmentToolsToRemove) {
        const { id, type } = toolToRemove;
        await this.props.removeAssessmentTool({
          id,
          type,
          parentId: resourceId,
          parentType,
          portalType,
        });
      }

      this.setState({
        isLoading: false,
        currentDialogInfo: { action: "", params: {} },
      });

      const setWorksheetBasicDetailsInfo = _.get(
        newWorksheetBasicDetailManager,
        "setWorksheetBasicDetailsInfo",
        _.noop
      );
      if (_.isFunction(setWorksheetBasicDetailsInfo)) {
        setWorksheetBasicDetailsInfo(prev => ({
          ...prev,
          showAssessmentTools: false,
        }));
      }
      return;
    }

    if (_.isEqual("MEASURE_ASSESSING", currentDialogInfo?.action)) {
      const { id, type } = this.updatedField;
      this.setState({ isLoading: true });
      await this.props.removeAssessmentTool({
        id,
        type,
        parentId: resourceId,
        parentType,
        portalType,
      });
      this.setState({
        isLoading: false,
        currentDialogInfo: { action: "", params: {} },
      });
      return;
    }

    const {
      key,
      newValue,
      oldValue,
      hasAddRemoveValues,
      ...extraParams
    } = this.updatedField;
    const fieldObj = _.find(this.props.resourceFields, { uid: key }) || {};
    const id = _.get(fieldObj, "id", "");
    const mutationParams = getPlannerFieldMutationParams({
      id,
      oldValue,
      hasAddRemoveValues,
      newValue,
    });
    const value = { ...mutationParams, portalType };

    const fieldTemplateObj = field_list[key];
    this.props.updateFieldInCache({
      fieldUID: key,
      params: value,
      fieldObj,
      fieldTemplateObj,
      extraParams,
    });

    this.props.updateField({
      key: id,
      params: value,
      fieldTemplateObj,
      type: this.props.parentType,
      t,
    });

    this.setState({ currentDialogInfo: { action: "", params: {} } });
  };

  handleScroll = () => {
    const scoreRef = this.Refs?.["score"];
    if (scoreRef && scoreRef?.state?.showPopOver) {
      scoreRef?.hidePopover?.();
    }
  };

  updateInputFieldLocal = (
    value,
    extraParams,
    isWarningReq,
    hasAddRemoveValues
  ) => {
    this.updateField({
      params: value,
      isMutation: true,
      extraParams,
      isWarningReq,
      hasAddRemoveValues,
    });
  };

  render() {
    const {
      t,
      step_list,
      steps,
      warningMsg,
      resourceId,
      parentType,
      portalType,
      field_list,
      footerComponent,
      isDraft,
      assessmentType,
      taskTypeGroup,
      assessmentMode,
      isFromInitializingModal,
      newWorksheetBasicDetailManager,
      resourceFields,
      assessmentTools,
      assignmentId,
      customClassForStep,
      testId,
    } = this.props;

    const isWorksheet =
      assessmentType === ASSESSMENT_TYPE_WORKSHEET ||
      taskTypeGroup === TASK_TYPE_WORKSHEET;

    const { isLoading, currentDialogInfo } = this.state;

    const { subjectDependencies, headerTitle, subTitle } =
      currentDialogInfo?.params || {};

    const currentDialogAction = _.get(currentDialogInfo, "action");

    const innerContainerClass = classNames({
      [classes.innerContainer]: true,
      [classes.aiTutorInnerContainer]: this.isAITutor(),
      [classes.noPadding]: isWorksheet || isFromInitializingModal,
    });

    const isWorksheetCreateOrEditModeAndNotFromInitializingModal =
      (assessmentType === ASSESSMENT_TYPE_WORKSHEET ||
        taskTypeGroup === TASK_TYPE_WORKSHEET) &&
      _.includes(["edit", "create"], assessmentMode) &&
      !isFromInitializingModal;

    let stepsV2 = isWorksheet
      ? mutateStepsForWorksheetSinglePageFlow({
          steps,
          newWorksheetBasicDetailManager,
          resourceFields,
          assessmentTools,
        })
      : steps;

    if (!getIsAIGradingEnabled()) {
      stepsV2 = _.filter(stepsV2, step => step !== "aiConfiguration");
    }

    const showSingleAssessmentCopyAlert =
      ACLStore.can("FeatureFlag:SingleAssessmentCopy") &&
      !_.isEmpty(assignmentId) &&
      assessmentMode === "edit" &&
      this.state.showSingleAssessmentCopyAlert;

    return (
      <>
        <div
          className={classes.container}
          ref={ref => (this.container = ref)}
          onScroll={this.handleScroll}
          data-test-id={`${testId}-container`}
        >
          {showSingleAssessmentCopyAlert ? (
            <Alert
              dsVersion="2.0"
              type={"warning"}
              message={t("classRoom:single_assessment_copy_alert_message")}
              isClosable={true}
              onClose={() =>
                this.setState(prev => ({
                  ...prev,
                  showSingleAssessmentCopyAlert: !prev.showSingleAssessmentCopyAlert,
                }))
              }
            />
          ) : null}
          <div
            className={innerContainerClass}
            onClick={this.onClickInnerContainer}
          >
            {warningMsg ? (
              <div className={classes.alertContainer}>
                <Alert type={"warning"} message={warningMsg} />
              </div>
            ) : null}

            {_.map(stepsV2, (key, index) => {
              const { fields, subtext, subText, config = {} } =
                step_list[key] || {};

              const { fieldStyles = {} } = config || {};

              const label = _.get(step_list[key], "label", "");
              const icon = _.get(step_list[key], "icon", null);

              const isLastStep = index !== _.size(stepsV2) - 1;
              const isLastSeparator = index === _.size(stepsV2) - 2;

              let subTextStyle;
              if (icon) {
                subTextStyle = styles.subTextStyle;
              }

              const separatorClass = classNames({
                [classes.separator]: true,
                [classes.aiTutorSeparator]: this.isAITutor() && isLastSeparator,
                [classes.minWidthUnset]: isWorksheet,
              });

              const stepContainerClasses = classNames(classes.stepContainer, {
                [classes.minWidthUnset]: isWorksheet,
                [customClassForStep]: !_.isEmpty(customClassForStep),
              });

              return (
                <React.Fragment key={key}>
                  <div className={stepContainerClasses}>
                    {!_.isEmpty(label) ? (
                      <div className={classes.stepLabel}>
                        <div className={classes.headerTxtContainer}>
                          <div className={classes.textContainer}>
                            <LabelComponent
                              t={t}
                              label={label}
                              showComment={false}
                              subText={subtext || subText}
                              icon={icon}
                              subTextStyle={subTextStyle}
                              {...styles.labelComponentStyles}
                            />
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <div className={classes.fieldContainer} style={fieldStyles}>
                      {this.renderFieldComponent(fields)}
                    </div>
                  </div>
                  {isLastStep &&
                  !isWorksheetCreateOrEditModeAndNotFromInitializingModal ? (
                    <div className={separatorClass} />
                  ) : null}
                </React.Fragment>
              );
            })}

            {React.isValidElement(footerComponent) ? footerComponent : null}
          </div>

          {currentDialogAction && currentDialogAction !== "REMOVE_SUBJECT" ? (
            <Modal
              hasOverlay={true}
              shouldCloseOnEsc={true}
              shouldCloseOnOverlayClick={true}
              width="440px"
              height="max-content"
              onClose={this.toggleDeleteDialogueBoxDisplay}
              isOpen={true}
            >
              <Modal.Header
                title={DIALOGS_INFO[currentDialogAction]?.title({
                  t,
                  currentDialogInfo,
                })}
              />
              <Modal.Body isScrollable={true}>
                {DIALOGS_INFO[currentDialogAction]?.body({
                  t,
                  currentDialogInfo,
                  updateCurrentDialogInfo: this.updateCurrentDialogInfo,
                  isDraft,
                })}
              </Modal.Body>
              <Modal.Footer>
                <footer className={classes.footerContainer}>
                  <Button
                    _legacy_variant={"outlined-subtle"}
                    variant="neutral"
                    type="outlined"
                    _legacy_size={"large"}
                    size="medium"
                    onClick={this.toggleDeleteDialogueBoxDisplay}
                  >
                    {DIALOGS_INFO[currentDialogAction]?.cancelButtonText({
                      t,
                    })}
                  </Button>
                  <Button
                    _legacy_size={"large"}
                    size="medium"
                    onClick={this.onConfirmUpdate}
                    disabled={DIALOGS_INFO[currentDialogAction]?.disabled?.({
                      currentDialogInfo,
                    })}
                  >
                    {DIALOGS_INFO[currentDialogAction]?.confirmButtonText({
                      t,
                      currentDialogInfo,
                    })}
                  </Button>
                </footer>
              </Modal.Footer>
            </Modal>
          ) : null}

          {currentDialogAction === "REMOVE_SUBJECT" ? (
            <RemoveSubjectModal
              parentId={resourceId}
              parentType={parentType}
              portalType={portalType}
              updateInputField={this.updateInputFieldLocal}
              subjectDependencies={subjectDependencies}
              headerTitle={headerTitle}
              removeButtonLabel={t("common:remove")}
              subtitle={subTitle}
              onRemove={this.onConfirmUpdate}
              onClose={this.toggleDeleteDialogueBoxDisplay}
              fieldList={field_list}
            />
          ) : null}
        </div>
        {isLoading ? (
          <FullScreenLoader overlayContainer={classes.overlayContainer} />
        ) : null}
      </>
    );
  }
}

const MiddlePaneWrapper = memo(props => {
  return (
    <ResourceDetailsContext.Consumer>
      {assessmentDetails => {
        const academicTermId = _.get(assessmentDetails, "academicTerm.id");
        const category = _.get(assessmentDetails, "category", {});
        const globalCategories = _.get(
          assessmentDetails,
          "globalCategories",
          []
        );
        const isLearningGoalsEvaluationEnabled = _.get(
          assessmentDetails,
          "isLearningGoalsEvaluationEnabled",
          false
        );

        const currentSubject = _.get(assessmentDetails, "currentSubject");
        const unitCourseId = _.get(
          assessmentDetails,
          "unitPlan.courses[0].id",
          ""
        );

        const selectedCourseId = _.get(assessmentDetails, "selectedCourseId");
        const weightage = _.get(assessmentDetails, "weightage");
        const preSelectedUnitPlan = _.get(assessmentDetails, "unitPlan", {});
        const creationMode = _.get(assessmentDetails, "creationMode");
        const isFromUnitPlan = _.get(
          assessmentDetails,
          "isFromUnitPlan",
          false
        );
        const isDraft = _.get(assessmentDetails, "isDraft", false);
        const assignmentId = _.get(assessmentDetails, "assignmentId", null);

        const prompts = _.get(assessmentDetails, "prompts", []);

        const gradeScales = _.get(assessmentDetails, "gradeScales");
        const assessmentGradeId = _.get(assessmentDetails, "grades[0].id");

        return (
          <MiddlePane
            {...props}
            academicTermId={academicTermId}
            category={category}
            globalCategories={globalCategories}
            isLearningGoalsEvaluationEnabled={isLearningGoalsEvaluationEnabled}
            currentSubject={currentSubject}
            selectedCourseId={selectedCourseId}
            weightage={weightage}
            preSelectedUnitPlan={preSelectedUnitPlan}
            creationMode={creationMode}
            isFromUnitPlan={isFromUnitPlan}
            isDraft={isDraft}
            assignmentId={assignmentId}
            prompts={prompts}
            gradeScales={gradeScales}
            unitCourseId={unitCourseId}
            assessmentGradeId={assessmentGradeId}
          />
        );
      }}
    </ResourceDetailsContext.Consumer>
  );
});

MiddlePaneWrapper.displayName = "MiddlePaneWrapper";

const BLANK_OBJECT = {};

const mapStateToProps = (state, ownProps) => {
  const { parentType, unitPlanTemplate } = ownProps;

  const aiData = _.get(state, "assessment.aiData", {});
  const updatedFields = _.get(aiData, "updatedFields");

  const { type } = _.get(state, "platform.currentCurriculumProgram", {});
  const curriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id",
    ""
  );

  const selectedCourseId = _.get(
    state,
    "teacher.selected_class.selected_course"
  );

  const courseId = ownProps?.selectedCourseId || selectedCourseId;

  const activeTab = _.get(state, "login.activeTab", "");

  return {
    focusedField:
      state.pusher.focusedField[enityEnumToLocalTypeMapping[parentType]],
    lockedFields:
      state.pusher.lockedFields[enityEnumToLocalTypeMapping[parentType]],
    orgId: _.get(state, "login.userInfo.org_id"),
    curriculumProgramType: type,
    courseId,
    curriculumProgramId,
    unitPlanFieldList: _.get(unitPlanTemplate, "field_list", BLANK_OBJECT),
    updatedFields,
    plannerElementSets: _.get(state, "platform.currentPlannerElementSets", {}),
  };
};

const mapActionCreators = {
  updateAIDataInRedux,
  hideIntercom,
  getCurrentAssessmentTypes,
  clearFieldUpdateActivityStatus,
  getRemovedSubjectDependencies,
  updateAssessment,
  updateAttachment,
  getSpeechToTextTranscript,
};

MiddlePaneWrapper.propTypes = {
  isLocalisedTemplate: PropTypes.bool,
  testId: PropTypes.string,
};

MiddlePaneWrapper.defaultProps = {
  isLocalisedTemplate: true,
  testId: "resourceEdit-middlePane",
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  I18nHOC({
    resource: ["unitPlanTemplate", "unitPlan", "common", "classRoom"],
  }),
  WithMiddlePaneHOC
)(MiddlePaneWrapper);

const WrapperFieldLabelComponent = memo(props => {
  const {
    label,
    subText,
    fieldKey,
    fieldId,
    prompts,
    defaultOptions,
    value,
    portalType,
    sourceType,
    onHelpClick,
    currentFieldKey,
    currentRightPane,
    helpEnabledFields,
    icon,
    extraRightButtonComponent,
    fieldTypeConfig,
    t,
    testId,
    submissionTemplateLoaderArr,
    resourceFields,
    curriculumType,
    type,
    onTurnitinAttachmentRename,
  } = props;

  const isLTIAlertApplicable =
    fieldKey === "score" && curriculumType === CURRICULUM_TYPE_DP;

  const { ltiServiceName, isLtiToolAdded } = getLtiToolData({
    resourceFields,
    isLTIAlertApplicable,
  });

  const { helpText: stepHelpText } = useMemo(
    () => helpEnabledFields[fieldKey] || {},
    [helpEnabledFields, fieldKey]
  );
  const extraLabelComponent = (
    <span className={classNames("text-body", classes.extraLabel)}>
      {t("project:optional_with_parenthesis")}
    </span>
  );

  const onHelpClickMemoized = useCallback(() => {
    onHelpClick({
      fieldKey,
      id: fieldId,
      helperTextId: stepHelpText.id,
    });
  }, [fieldKey, fieldId, stepHelpText]);

  const subTextStyle = useMemo(() => {
    if (icon) {
      return styles.subTextStyle;
    }
  }, [icon]);

  const labelComponent = label ? (
    <LabelComponent
      t={t}
      extraRightButtonComponent={extraRightButtonComponent}
      icon={icon}
      label={label}
      labelCoachmarkId={
        fieldKey == "resources" ? `COACHMARKS_RESOURCE_${fieldId}` : undefined
      }
      promptsName={"Planning Prompts"}
      prompts={prompts}
      showHelper={!_.isEmpty(stepHelpText)}
      isHelpActive={currentRightPane == "help" && currentFieldKey == fieldKey}
      onHelpClick={onHelpClickMemoized}
      showComment={false}
      plannerIconProps={styles.labelComponentStyles.plannerIconProps}
      leftContainerStyle={{
        ...styles.labelComponentStyles.leftContainerStyle,
        ..._.get(fieldTypeConfig, "leftContainerStyle", {}),
      }}
      labelIconContainerStyle={
        styles.labelComponentStyles.labelIconContainerStyle
      }
      labelStyle={{
        ..._.get(fieldTypeConfig, "labelStyle", {}),
        ...styles.labelComponentStyles.labelStyle,
      }}
      showSubtext={true}
      subText={subText}
      subTextStyle={subTextStyle}
      helpIconProps={{ size: "small" }}
      extraLabelComponent={
        _.includes(["aiInstructions", "tutorSecondaryLanguage"], fieldKey)
          ? extraLabelComponent
          : null
      }
    />
  ) : null;

  const isDragAndDropEnabled =
    ACLStore.can("FeatureFlag:RearrangeOrganizationResource") &&
    (fieldKey === "studentTemplate" || fieldKey === "plannerResources") &&
    _.isEmpty(submissionTemplateLoaderArr);

  return (
    <>
      <DSProvider dsVersion={type === "RichText" ? "1.0" : "2.0"}>
        <FieldComponent
          {...props}
          value={JSON.parse(value)}
          labelComponent={labelComponent}
          defaultOptions={JSON.parse(defaultOptions)}
          portalType={portalType}
          sourceType={sourceType}
          isAssessmentCreationRevamp={true}
          renderNewEditor={ACLStore.can(
            "FeatureFlag:ShowLexicalRTEInAssessments"
          )}
          isDragAndDropEnabled={isDragAndDropEnabled}
          testId={testId}
          isLtiToolAdded={isLtiToolAdded}
          dsVersion="2.0"
          onTurnitinAttachmentRename={onTurnitinAttachmentRename}
        />
      </DSProvider>

      {isLTIAlertApplicable && isLtiToolAdded ? (
        <div className={classes.scoreAlertContainer}>
          <Alert
            dsVersion="2.0"
            type="info"
            message={t("classRoom:max_score_lti_tool_alert_message", {
              serviceName: getLTIName(ltiServiceName) || ltiServiceName,
            })}
          />
        </div>
      ) : null}
    </>
  );
});

WrapperFieldLabelComponent.displayName = "WrapperFieldLabelComponent";
